<template>
  <DetailCard title="Dados profissionais">
    <div class="flex flex-col gap-5">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <DetailField :icon="mdiAccountOutline" label="Nome registrado no CRM">
          <span data-testid="doctor-crm-name">{{ crmName || '—' }}</span>
        </DetailField>

        <DetailField :icon="mdiCardAccountDetailsOutline" label="Número do CRM">
          <span data-testid="doctor-crm-number">
            {{ doctor.doctor.mainCrm.number }}-{{ doctor.doctor.mainCrm.uf }}
          </span>
        </DetailField>

        <DetailField
          v-if="doctor.doctor.mainCrm.status"
          :icon="mdiCheckDecagramOutline"
          label="Status do CRM"
        >
          {{ doctor.doctor.mainCrm.status }}
        </DetailField>

        <DetailField
          v-if="doctor.doctor.mainCrm.type"
          :icon="mdiBadgeAccountOutline"
          label="Tipo do CRM"
        >
          {{ doctor.doctor.mainCrm.type }}
        </DetailField>
      </div>

      <!--
        Modelo 1 doctor : N doctor_crm (transferência de UF): a lista completa
        só aparece quando há mais de um CRM — com um único, os campos acima já
        contam a história toda.
      -->
      <div v-if="hasMultipleCrms" class="pt-5 border-t border-gray-100">
        <p class="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">CRMs do médico</p>
        <ul class="flex flex-col gap-2">
          <li
            v-for="crm in crms"
            :key="crm.id"
            data-testid="doctor-crm-item"
            class="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-2.5"
          >
            <span class="text-sm font-medium text-gray-900">{{ crm.number }}-{{ crm.uf }}</span>
            <span
              v-if="crm.isMain"
              data-testid="doctor-crm-main-badge"
              class="inline-flex items-center rounded-full bg-primary5 border border-primary px-2 py-0.5 text-xs font-medium text-primary"
            >
              Principal
            </span>
            <span v-if="crm.status" class="text-xs text-gray-500 ml-auto">{{ crm.status }}</span>
          </li>
        </ul>
      </div>

      <div v-if="hasSpecialties" class="pt-5 border-t border-gray-100">
        <p class="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">Especialidades</p>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="doctorSpecialty in doctor.doctor.mainCrm.doctorSpecialties"
            :key="doctorSpecialty.id"
            class="inline-flex items-center gap-2 rounded-lg border border-primary bg-primary5 px-3 py-1.5"
          >
            <v-icon size="16" color="primary">{{ mdiStethoscope }}</v-icon>
            <span class="text-sm font-medium text-textPrimary">
              {{ doctorSpecialty.specialty.name }}
            </span>
            <span
              v-if="doctorSpecialty.rqe"
              class="text-xs text-primary/80 border-l border-primary/30 pl-2"
            >
              RQE {{ doctorSpecialty.rqe }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </DetailCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  mdiAccountOutline,
  mdiCardAccountDetailsOutline,
  mdiCheckDecagramOutline,
  mdiBadgeAccountOutline,
  mdiStethoscope
} from '@mdi/js'

import type { DoctorDetailResponse } from '@/Network/Types/Responses/CompanyResponses'
import type { DoctorCrmResponse } from '@/Network/Types/Responses/DoctorCrmResponses'
import DetailCard from '../atoms/DetailCard.vue'
import DetailField from '../atoms/DetailField.vue'
import { toTitleCase } from '../../helpers/doctorStatus'

const props = withDefaults(
  defineProps<{
    doctor: DoctorDetailResponse
    crms?: DoctorCrmResponse[]
  }>(),
  { crms: () => [] }
)

const crmName = computed(() => toTitleCase(props.doctor.doctor.mainCrm.name))

const hasMultipleCrms = computed(() => props.crms.length > 1)

const hasSpecialties = computed(
  () =>
    props.doctor.doctor.mainCrm.doctorSpecialties &&
    props.doctor.doctor.mainCrm.doctorSpecialties.length > 0
)
</script>
